import { useCallback, useEffect, useState } from 'react';
import axios, { Axios, AxiosError } from 'axios';
import { getApiUrl, getContractsUrl,getContractsAPIUrl,getContactAPIUrl } from '../config';
import { useUser } from '../Context/UserContext';
import { CookieService } from '../services/CookieService';
const useAPI = () => {
    const { bImpersonating } = useUser();

    // const apiUrl = process.env.REACT_APP_API_URL || 'https://webapi.icegems.com/api'; put back in config.json "http://127.0.0.1:8000/api"
    const apiUrl = getApiUrl(); // get api url from config.json
    const contractsUrl = getContractsUrl(); // get contracts url from config.json
    const contractsAPIUrl = getContractsAPIUrl();
    const contactAPIUrl = getContactAPIUrl();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    const verifyToken = useCallback(async (token: string) => {
        try {
            const response = await axios.get(`${apiUrl}/auth/verify`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.status === 200;
        } catch (error) {
            return false;
        }
    }, [apiUrl]);

    const getUsers = useCallback(async (searchname: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/users`, {
                params: {
                    searchname
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get users');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserById = useCallback(async (userId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/users/${userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get user');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserRoles = useCallback(async (userId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/user-roles/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const assignRoleToUser = useCallback(async (userId: string, roleId: string | number) => {
        try {
            const token = CookieService.getAuthToken();

            // Ensure userId is a valid string
            if (!userId || typeof userId !== 'string') {
                throw new Error(`Invalid userId: ${userId}`);
            }

            // Convert roleId to string if it's a number
            const roleIdStr = String(roleId).trim();

            // Make sure roleId is valid after conversion
            if (!roleIdStr) {
                throw new Error(`Invalid roleId: ${roleId}`);
            }

            const requestData = {
                userId: userId.trim(),
                roleId: roleIdStr
            };

            const response = await axios.post(`${apiUrl}/admin/assign-role`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const removeRoleFromUser = useCallback(async (userId: string, roleId: string | number) => {
        try {
            const token = CookieService.getAuthToken();

            // Ensure userId is a valid string
            if (!userId || typeof userId !== 'string') {
                throw new Error(`Invalid userId: ${userId}`);
            }

            // Convert roleId to string if it's a number
            const roleIdStr = String(roleId).trim();

            // Make sure roleId is valid after conversion
            if (!roleIdStr) {
                throw new Error(`Invalid roleId: ${roleId}`);
            }

            const requestData = {
                userId: userId.trim(),
                roleId: roleIdStr
            };

            const response = await axios.post(`${apiUrl}/admin/remove-role`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: requestData
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const createUser = useCallback(async (userData: any, addedByID: string) => {
        try {
            const token = CookieService.getAuthToken();
            // Extract roles from userData to handle separately
            const { roles, ...userDataWithoutRoles } = userData;

            const response = await axios.post(`${apiUrl}/admin/create-user`, userDataWithoutRoles, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });


            // Get the new user's ID from the response
            const newUserId = response.data && response.data.id;

            if (!newUserId) {
                throw new Error('Failed to get ID for newly created user');
            }

            const metadata = JSON.stringify({
                email: userDataWithoutRoles.email,
                device: isMobile ? "mobile" : "desktop"
            });
            addUserTracking(newUserId, "Create User", addedByID, metadata)
            // If roles array is provided, assign each role to the user
            if (newUserId && roles && Array.isArray(roles) && roles.length > 0) {
                const roleAssignPromises = roles.map(roleId => {
                    return assignRoleToUser(newUserId, roleId);
                });
                await Promise.all(roleAssignPromises);
            }
            // For backward compatibility - if single role is provided in the old format
            else if (newUserId && userData.role) {
                await assignRoleToUser(newUserId, userData.role);
            }

            return response;
        } catch (error) {
            throw error;
        }
    }, [apiUrl, assignRoleToUser]);

    const updateUser = useCallback(async (userId: string, userData: any, addedByID: string) => {
        try {
            // Ensure userId is valid
            if (!userId || typeof userId !== 'string') {
                throw new Error(`Invalid userId: ${userId}`);
            }

            const token = CookieService.getAuthToken();
            // Extract roles from userData to handle separately
            const { roles, ...userDataWithoutRoles } = userData;

            // Add id to the user data - this is critical for the server to identify the user
            const userDataToSend = {
                ...userDataWithoutRoles,
                id: userId  // Explicitly include ID in the request payload
            };

            //console.log("userDataToSend: ", userDataToSend);

            // First update user basic data
            const response = await axios.put(`${apiUrl}/admin/update-user`, userDataToSend, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // If roles array is provided, update roles
            if (roles && Array.isArray(roles)) {
                // Get current roles
                const currentRoles = await getUserRoles(userId);

                // Process current roles - ensure we have clean IDs
                const currentRoleIds = Array.isArray(currentRoles)
                    ? currentRoles.map(role => typeof role === 'string' ? role : role.id)
                    : [];

                // More robust filtering logic with better error handling
                const newRoleIds = roles
                    .filter(id => id !== null && id !== undefined) // Filter out null/undefined
                    .map(id => {
                        if (typeof id !== 'string') {
                            // Convert to string if possible
                            return String(id).trim();
                        }
                        return id.trim();
                    });

                // When no current roles exist but new roles are being added, or vice versa, 
                // consider it a change without further comparison
                let rolesChanged = (currentRoleIds.length === 0 && newRoleIds.length > 0) ||
                    (currentRoleIds.length > 0 && newRoleIds.length === 0);

                // If not an obvious case, do a detailed comparison
                if (!rolesChanged) {
                    rolesChanged = (
                        newRoleIds.length !== currentRoleIds.length ||
                        newRoleIds.some(id => !currentRoleIds.includes(id)) ||
                        currentRoleIds.some(id => !newRoleIds.includes(id))
                    );
                }

                if (rolesChanged) {
                    // Calculate roles to remove (in current but not in new)
                    const rolesToRemove = currentRoleIds.filter(id => !newRoleIds.includes(id));

                    // Calculate roles to add (in new but not in current)
                    const rolesToAdd = newRoleIds.filter(id => !currentRoleIds.includes(id));

                    // Remove roles that should be removed
                    if (rolesToRemove.length > 0) {
                        const removePromises = rolesToRemove.map(roleId => {
                            return removeRoleFromUser(userId, roleId);
                        });
                        await Promise.all(removePromises);
                    }

                    // Add new roles
                    if (rolesToAdd.length > 0) {
                        const assignPromises = rolesToAdd.map(roleId => {
                            return assignRoleToUser(userId, roleId);
                        });
                        await Promise.all(assignPromises);
                    }
                }
            }
            // For backward compatibility - if single role is provided in the old format
            else if (userData.role) {
                // Get current roles
                const currentRoles = await getUserRoles(userId);

                // Remove all current roles
                if (currentRoles && Array.isArray(currentRoles)) {
                    const removePromises = currentRoles.map(role =>
                        removeRoleFromUser(userId, typeof role === 'string' ? role : role.id)
                    );
                    await Promise.all(removePromises);
                }

                // Assign the new role
                await assignRoleToUser(userId, userData.role);
            }

            const metadata = JSON.stringify({
                email: userDataWithoutRoles.email,
                device: isMobile ? "mobile" : "desktop"
            });
            addUserTracking(userId, "Update User", addedByID, metadata)
            return response;
        } catch (error) {
            throw error;
        }
    }, [apiUrl, getUserRoles, assignRoleToUser, removeRoleFromUser]);


    const login = useCallback(async (email: string, password: string) => {
        try {
            const response = await axios.post(`${apiUrl}/auth/login`, {
                email,
                password
            });

            const metadata = JSON.stringify({
                app: "IceGems",
                device: isMobile ? "mobile" : "desktop"
            });

            addUserTracking(response.data.user.id, "Login", response.data.user.id, metadata)
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle specific Axios error
                throw new Error(error.response?.data?.message || 'Login failed');
            }
            throw error;
        }
    }, [apiUrl]);

    const register = useCallback(async (userData: {
        email: string;
        password: string;
        firstName: string;
        middleName?: string;
        lastName: string;
        streetaddress?: string;
        streetaddress2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
        age?: number;
        phone?: string;
        companyName?: string;
        companyType?: string;
        title?: string;
        profession: string[];
        professionOther?: string;
        interests: string[];
        interestsOther?: string;
        cidn?: number;
    }) => {
        try {
            const device = isMobile ? "mobile" : "desktop"
            const response = await axios.post(`${apiUrl}/auth/register`, { device, ...userData });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const forgotPassword = useCallback(async (email: string) => {
        try {
            const response = await axios.post(`${apiUrl}/auth/forgot-password`, { email });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const confirmEmail = useCallback(async (token: string) => {
        try {
            const device = isMobile ? "mobile" : "desktop"
            const response = await axios.post(`${apiUrl}/auth/confirm-email`, { token, device });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const resetPassword = useCallback(async (token: string, newPassword: string) => {
        try {
            const device = isMobile ? "mobile" : "desktop"
            const response = await axios.post(`${apiUrl}/auth/reset-password`, { token, newPassword, device });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);

    const updateUserPassword = useCallback(async (userId: string, newPassword: string, addedByID: string, email: string, source: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.put(`${apiUrl}/users/${userId}/password`, { newPassword, addedByID }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const metadata = JSON.stringify({
                email: email,
                source: source,
                device: isMobile ? "mobile" : "desktop"
            });

            addUserTracking(userId, "Changed Password", addedByID, metadata)
            return response.data;
        } catch (error) {
            throw error;
        }
    }, [apiUrl]);
    const getRoles = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get roles');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserInfoReportsList = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/files/protected/reports/user-info`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to UserInfoReprotsList');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserInfoReport = useCallback(async (fileName: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/files/protected/reports/user-info/${fileName}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // <-- Set response type to Blob
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || `Failed to get User Info Report ${fileName}`);
            }
            throw error;
        }
    }, [apiUrl]);

    const getNewsOtp = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/files/otp/video1`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get news otps');
            }
            throw error;
        }
    }, [apiUrl]);


    const getOpt = useCallback(async (videoName: string) => {
    try {
        const response = await axios.get(`${apiUrl}/files/otp/${videoName}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.message || `Failed to get ${videoName} otp`);
        }
        throw error;
    }
}, [apiUrl]);

    const getBuisnessFile = useCallback(async (fileName: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/files/protected/${fileName}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // <-- Set response type to Blob
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get roles');
            }
            throw error;
        }
    }, [apiUrl]);

    const createRole = useCallback(async (name: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.post(`${apiUrl}/admin/new-role`, { name }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to create role');
            }
            throw error;
        }
    }, [apiUrl]);

    const updateRole = useCallback(async (id: string, name: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.put(`${apiUrl}/admin/roles/${id}`, { name }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to update role');
            }
            throw error;
        }
    }, [apiUrl]);

    const getPermissions = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/permissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get permissions');
            }
            throw error;
        }
    }, [apiUrl]);

    const createPermission = useCallback(async (key: string, description: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.post(`${apiUrl}/admin/permissions`, { key, description }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to create permission');
            }
            throw error;
        }
    }, [apiUrl]);

    const updatePermission = useCallback(async (id: string, key: string, description: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.put(`${apiUrl}/admin/permissions/${id}`, { key, description }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to update permission');
            }
            throw error;
        }
    }, [apiUrl]);

    const deleteRole = useCallback(async (id: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.delete(`${apiUrl}/admin/roles/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete role');
            }
            throw error;
        }
    }, [apiUrl]);
    const getRolePermissions = useCallback(async (roleId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/role-permissions/${roleId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data; // array of permission IDs or full permission objects
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get role permissions');
            }
            throw error;
        }
    }, [apiUrl]);

    const assignPermissionToRole = useCallback(async (roleId: string, permissionId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.post(`${apiUrl}/admin/assign-permission`, {
                roleId,
                permissionId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to assign permission to role');
            }
            throw error;
        }
    }, [apiUrl]);

    const removePermissionFromRole = useCallback(async (roleId: string, permissionId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.delete(`${apiUrl}/admin/role-permissions`, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                data: {
                    roleId,
                    permissionId
                }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to remove permission from role');
            }
            throw error;
        }
    }, [apiUrl]);
    const getUserPermissions = useCallback(async (userId: string) => {
        try {
            if (!userId || typeof userId !== 'string') {
                throw new Error('Invalid userId provided');
            }

            const token = CookieService.getAuthToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(`${apiUrl}/users/user-permissions/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to fetch user permissions');
            }
            throw error;
        }
    }, [apiUrl]);

    const assignPermissionToUser = useCallback(async (userId: string, permissionId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.post(`${apiUrl}/admin/user-permissions`, {
                userId,
                permissionId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to assign permission to user');
            }
            throw error;
        }
    }, [apiUrl]);

    const removePermissionFromUser = useCallback(async (userId: string, permissionId: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.delete(`${apiUrl}/admin/user-permissions`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { userId, permissionId }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to remove permission from user');
            }
            throw error;
        }
    }, [apiUrl]);

    const getMyRoles = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/users/my-roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to fetch user roles');
            }
            throw error;
        }
    }, [apiUrl]);

    const getMyPermissions = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/users/my-permissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to fetch user permissions');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserRegisteredRoleId = useCallback(async (token: string) => {
        try {
            const response = await axios.get(`${apiUrl}/admin/roles/user-registered`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data.id;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get user-registered role ID');
            }
            throw error;
        }
    }, [apiUrl]);

    const deleteUser = useCallback(async (userId: string, addedByID: string, email: String) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.delete(`${apiUrl}/admin/delete-user/${userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const metadata = JSON.stringify({
                email: email,
                device: isMobile ? "mobile" : "desktop"
            });
            UpdateAgreementSetInactive(null,userId,"User Deleted",addedByID)
            addUserTracking(userId, "Delete User", addedByID, metadata)
            return response;

        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete user');
            }
            throw error;
        }
    }, [apiUrl]);

    const addUserTracking = useCallback(async (userID: string, activityName: string, addedByID: string, metadata: string) => {
        // Don't execute tracking when impersonating
        if (bImpersonating) {
            console.log('User tracking skipped - currently impersonating');
            return;
        }
        // Log the activity to the database
        try {
            const response = await axios.post(`${apiUrl}/users/AddUserTracking`, { userID, activityName, addedByID, metadata });
            return response;
        } catch (error) {
            throw error;
        }
    }, [apiUrl, bImpersonating]);




    const getContractTypes = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/ContractTypes`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contract Types');
            }
            throw error;
        }
    }, [apiUrl]);

    const getContractStatuses = useCallback(async (contractType: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/ContractStatuses/${contractType}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get Contract Statuses');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserContractStatus = useCallback(async (contractStatus: string, contractType: string, signerName: string, companyName: string) => {
        try {
            const token = CookieService.getAuthToken();

            const response = await axios.get(`${apiUrl}/admin/UserContracts`, {
                params: {
                    contractStatus,
                    contractType,
                    signerName,
                    companyName
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response;


        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get User contract statuses');
            }
            throw error;
        }
    }, [apiUrl]);


    const getUserTrackingPages = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/UserTrackingPages`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get User Tracking Pages');
            }
            throw error;
        }
    }, [apiUrl]);

    const getActivityTypes = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/ActivityTypes`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get Activity Types');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUserActivity = useCallback(async (userId: string, activityType: string, pageName: string) => {
        try {
            const token = CookieService.getAuthToken();
            if (activityType !== "Page Visited") {
                pageName = "all";
            }
            const response = await axios.get(`${apiUrl}/admin/UserActivity`, {
                params: {
                    userId,
                    activityType,
                    pageName
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response;


        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get User Activity');
            }
            throw error;
        }
    }, [apiUrl]);

    const postSendReqInfoEmail = useCallback(async (email: string, firstName: string, lastName: string, title: string, requInfo: string[], message: string, companyName: string) => {
        try {
            const response = await axios.post(`${apiUrl}/req-info`, {
                email,
                firstName,
                lastName,
                title,
                requInfo,
                message,
                companyName
            });

            const metadata = JSON.stringify({
                app: "IceGems",
                device: isMobile ? "mobile" : "desktop"
            });

            //addUserTracking(response.data.user.id, "Login", response.data.user.id, metadata)
            //return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle specific Axios error
                throw new Error(error.response?.data?.message || 'Login failed');
            }
            throw error;
        }
    }, [apiUrl]);

    const postUserData = useCallback(async (email: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.post(`${apiUrl}/users/user-data`,
                { email },
                {
                    headers: { Authorization: `Bearer ${token}` }
                });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || `Failed to get User data on ${email}`);
            }
            throw error;
        }
    }, [apiUrl]);

     const getStateProvince = useCallback(async () => {
        try {
            const response = await axios.get(`${apiUrl}/admin/StateProvince`);
            //console.log("response:", response.data);
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get state province list');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUsersDropdwn = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/UsersDropdown`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get users dropdown');
            }
            throw error;
        }
    }, [apiUrl]);

      const getUserAgreements = useCallback(async (userid: string,agreementtypeid: number, status: string,cidn: number) => {
        try {
           
            // Make sure userid is valid 
            if (!userid) {
                throw new Error(`Invalid agreement: ${userid}`);
            }
            const response = await axios.get(`${contractsAPIUrl}/contract/AgreementsByUserID`,  {
                params: {
                    userid,
                    agreementtypeid,
                    status,
                    cidn
                }
            });
            
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get User Agreements');
            }
            throw error;
        }
    }, [contractsAPIUrl]);


    const saveContact = useCallback(async (userData: any, bReturnAll: boolean, cidn?: Number | null) => {
        try {
        const payload = { ...userData };
        if (cidn) payload.cidn = cidn; // include only if exists
        payload.breturnall = bReturnAll;

        console.log("payload: ", payload);
        console.log("cidn from useAPI: ", cidn);

        const response = await axios.post(`${contactAPIUrl}/contact/contact`, payload);
        // Destructure the first row from the array
        const [firstRow] = response.data;

        //console.log("firstRow: ", firstRow);
        
        return firstRow;

        } catch (error) {
        console.error("Error saving contact:", error);
        throw error;
        }
    }, [apiUrl]);

    const getContacts = useCallback(async (searchname: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/GetContacts`, {
                params: {
                    searchname
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contacts');
            }
            throw error;
        }
    }, [apiUrl]);

     const deleteContact = useCallback(async (cidn: number, addedByID: string, email: String) => {
        try {
            const token = CookieService.getAuthToken();

            const response = await axios.delete(`${contactAPIUrl}/contact/delete-contact/${cidn}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            return response;

        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete contact');
            }
            throw error;
        }
    }, [apiUrl]);

    const deleteContactPhone = useCallback(async (contactphoneid: number) => {
        try {
            const token = CookieService.getAuthToken();

            const response = await axios.delete(`${contactAPIUrl}/contact/delete-contactphone/${contactphoneid}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            return response;

        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete contact phone');
            }
            throw error;
        }
    }, [apiUrl]);


    const getContactByCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact by cidn');
            }
            throw error;
        }
    }, [apiUrl]);

    const getUsersByContactCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${apiUrl}/admin/UsersByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get users by cidn');
            }
            throw error;
        }
    }, [apiUrl]);

    const getContactAddressByCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactAddressByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact address by cidn');
            }
            throw error;
        }
    }, [apiUrl]);

    const getContactEmailByCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactEmailByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact email by cidn');
            }
            throw error;
        }
    }, [apiUrl]);

    const getContactPhoneByCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactPhoneByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact phone by cidn');
            }
            throw error;
        }
    }, [apiUrl]);

    const getContactCompanyByCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactCompanyByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact company by cidn');
            }
            throw error;
        }
    }, [apiUrl]);

    const getContactCRMByCIDN = useCallback(async (cidn: number) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactCRMByCIDN`, {
                params: {
                    cidn
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact CRM by cidn');
            }
            throw error;
        }
    }, [apiUrl]);


     const updateContactPhone = useCallback(async (contactphoneid: Number | null, editedPhone: any, cidn: Number | null) => {
        try {
        const payload = { ...editedPhone };
        if (contactphoneid) payload.contactphoneid = contactphoneid; // include only if exists       
         if (cidn) payload.cidn = cidn; 

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdatePhone`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error saving contact phone number:", error);
        throw error;
        }
    }, [apiUrl]);

    const addContactPhone = useCallback(async (newPhone: any, cidn: Number | null) => {
        try {
        const payload = { ...newPhone };
        if (cidn) payload.cidn = cidn; 
        payload.contactphoneid = 0; //send as 0 so we know its an insert

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdatePhone`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error saving contact phone number:", error);
        throw error;
        }
    }, [apiUrl]);

    
     const updateContactCompany = useCallback(async (contactcompanyid: Number | null, editedCompany: any, cidn: Number | null) => {
        try {
        const payload = { ...editedCompany };
        if (contactcompanyid) payload.contactcompanyid = contactcompanyid; // include only if exists       
         if (cidn) payload.cidn = cidn; 

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdateCompany`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error saving contact company:", error);
        throw error;
        }
    }, [apiUrl]);

    const addContactCompany = useCallback(async (newCompany: any, cidn: Number | null) => {
        try {
        const payload = { ...newCompany };
        if (cidn) payload.cidn = cidn; 
        payload.contactcompanyid = 0; //send as 0 so we know its an insert

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdateCompany`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error saving contact company:", error);
        throw error;
        }
    }, [apiUrl]);

    const deleteContactCompany = useCallback(async (contactcompanyid: number) => {
        try {
            const token = CookieService.getAuthToken();

            const response = await axios.delete(`${contactAPIUrl}/contact/delete-contactcompany/${contactcompanyid}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            return response;

        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete contact');
            }
            throw error;
        }
    }, [apiUrl]);

    const updateContactEmail = useCallback(async (contactemailid: Number | null, editedEmail: any, cidn: Number | null) => {
        try {
        const payload = { ...editedEmail };
        if (contactemailid) payload.contactcompanyid = contactemailid; // include only if exists       
         if (cidn) payload.cidn = cidn; 

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdateEmail`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error updating contact email:", error);
        throw error;
        }
    }, [apiUrl]);

    const addContactEmail = useCallback(async (newEmail: any, cidn: Number | null) => {
        try {
        const payload = { ...newEmail };
        if (cidn) payload.cidn = cidn; 
        payload.contactemailid = 0; //send as 0 so we know its an insert

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdateEmail`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error adding contact email:", error);
        throw error;
        }
    }, [apiUrl]);

    const deleteContactEmail = useCallback(async (contactemailid: number) => {
        try {
            const token = CookieService.getAuthToken();

            const response = await axios.delete(`${contactAPIUrl}/contact/delete-contactemail/${contactemailid}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            return response;

        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete contact email');
            }
            throw error;
        }
    }, [apiUrl]);

    const updateContactAddress = useCallback(async (contactaddressid: Number | null, editedAddress: any, cidn: Number | null) => {
        try {
        const payload = { ...editedAddress };
        if (contactaddressid) payload.contactaddressid = contactaddressid; // include only if exists       
         if (cidn) payload.cidn = cidn; 

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdateAddress`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error updating contact address:", error);
        throw error;
        }
    }, [apiUrl]);

   const checkContactForMatch = useCallback(async (userData: any, bmatchonly: boolean) => {
        try {
        const payload = { ...userData };
        payload.bmatchonly = bmatchonly;

        console.log("payload: ", payload);

    
         const response = await axios.post(`${contactAPIUrl}/contact/checkContactForMatch`, payload);
        
         const [firstRow] = response.data;

        //console.log("firstRow: ", firstRow);
        
        return firstRow;

        } catch (error) {
        console.error("Error checking Contact For Match:", error);
        throw error;
        }
    }, [contactAPIUrl]);
    const addContactAddress = useCallback(async (newAddress: any, cidn: Number | null) => {
        try {
        const payload = { ...newAddress };
        if (cidn) payload.cidn = cidn; 
        payload.contactaddressid = 0; //send as 0 so we know its an insert

        const response = await axios.post(`${contactAPIUrl}/contact/ContactUpdateAddress`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error adding contact address:", error);
        throw error;
        }
    }, [apiUrl]);

    const deleteContactAddress = useCallback(async (contactaddressid: number) => {
        try {
            const token = CookieService.getAuthToken();

            const response = await axios.delete(`${contactAPIUrl}/contact/delete-contactaddress/${contactaddressid}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            return response;

        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to delete contact address');
            }
            throw error;
        }
    }, [apiUrl]);

     const addUserToContact = useCallback(async (cidn: Number | null, id: string | null) => {
        try {
        const token = CookieService.getAuthToken();

        // Ensure userId is a valid string
        if (!id || typeof id !== 'string') {
            throw new Error(`Invalid id: ${id}`);
        }

        const requestData = {
            cidn: Number(cidn),
            id: id.trim()            
        };

        const response = await axios.post(`${apiUrl}/admin/AddUserToContact`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;


        } catch (error) {
        console.error("Error adding user to contact:", error);
        throw error;
        }
    }, [apiUrl]);

     const checkEmailExists = useCallback(async (email: string) => {
        try {

        // Make sure entityId is valid 
        if (!email) {
            throw new Error(`Invalid email: ${email}`);
        }

        const response = await axios.get(`${apiUrl}/admin/CheckEmailExists`,  {
                params: {
                    email
                }
            });
        
        // Return true if an object is returned (email exists), false otherwise
        return response.data && Object.keys(response.data).length > 0;

        } catch (error) {
        console.error("Error checking if email exists:", error);
        throw error;
        }
    }, [apiUrl]);

    const getContactByEmail = useCallback(async (email: string) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactByEmail`, {
                params: {
                    email
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contact by email');
            }
            throw error;
        }
    }, [apiUrl]);

    
     const updateContact = useCallback(async (cidn: Number | null, editedContact: any) => {
        try {
        const payload = { ...editedContact };
         if (cidn) payload.cidn = cidn; 

        const response = await axios.post(`${contactAPIUrl}/contact/UpdateContact`, payload);
        
        // Destructure the first row from the array
        const [firstRow] = response.data;

        return firstRow;

        } catch (error) {
        console.error("Error saving contact:", error);
        throw error;
        }
    }, [apiUrl]);
       
    const updateUserPrimaryInd = useCallback(async (cidn: number, id: string, cidnprimaryind: boolean) => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.post(`${apiUrl}/admin/user-cidnprimary`, {
                cidn,
                id,
                cidnprimaryind
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to update cidn primary indicator');
            }
            throw error;
        }
    }, [apiUrl]);

    const copyContactData = useCallback(async (old_cidn: Number | null, cidn: number | null, id: string | null) => {
        try {
        const token = CookieService.getAuthToken();

        const requestData = {
            old_cidn: Number(old_cidn),
            cidn: Number(cidn),
            id: id,            
        };

        const response = await axios.post(`${contactAPIUrl}/contact/CopyContactData`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;


        } catch (error) {
        console.error("Error copying contact:", error);
        throw error;
        }
    }, [apiUrl]);

    const removeUserFromContact = useCallback(async (cidn: number | null, id: string | null) => {
        try {
        const token = CookieService.getAuthToken();

        const requestData = {
            cidn: Number(cidn),
            id: id,            
        };

        const response = await axios.post(`${contactAPIUrl}/contact/RemoveUserFromContact`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;

        } catch (error) {
        console.error("Error removing user from contact:", error);
        throw error;
        }
    }, [apiUrl]);
    
    
    const UpdateAgreementSetInactive = useCallback(async (documentcode: string | null,userid: string | null,comments: string, inactivatedby: string) => {
    try {
        
        
        const response = await axios.put(`${contractsAPIUrl}/contract/UpdateAgreementSetInactive`, { documentcode, userid,comments, inactivatedby});
        
        return response;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.message || 'Failed to set Agreements to inactive');
        }
        throw error;
    }
    }, [contractsAPIUrl]);
    

 const getContactsToMatch = useCallback(async () => {
        try {
            const token = CookieService.getAuthToken();
            const response = await axios.get(`${contactAPIUrl}/contact/ContactsToMatch`, {
                
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get contacts to match');
            }
            throw error;
        }
    }, [contactAPIUrl]);


const UpdateContactMatchStatus = useCallback(async (cidn: Number, cidn_match: number, status: string,reviewedby: string) => {
        try {
        const token = CookieService.getAuthToken();

        const requestData = {
            cidn: Number(cidn),
            cidn_match: Number(cidn_match),
            matchstatus: status,
            reviewedby: reviewedby            
        };

        const response = await axios.post(`${contactAPIUrl}/contact/ContactMatchStatus`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;


        } catch (error) {
        console.error("Error updating contact match status:", error);
        throw error;
        }
    }, [contactAPIUrl]);


    const ContactMatch = useCallback(async (cidn: Number, cidn_match: number, firstname: string,middlename: string,lastname: string,contactaddressid: number,contactemailid: number,contactphoneid: number,contactcompanyid: number) => {
        try {
        const token = CookieService.getAuthToken();

        const requestData = {
            cidn: Number(cidn),
            cidn_match: Number(cidn_match),
            firstname: firstname,
            middlename: middlename,
            lastname: lastname,
            contactaddressid: contactaddressid,
            contactemailid: contactemailid,
            contactphoneid: contactphoneid,
            contactcompanyid: contactcompanyid
        };

        const response = await axios.post(`${contactAPIUrl}/contact/ContactMatch`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;


        } catch (error) {
        console.error("Error updating contact match:", error);
        throw error;
        }
    }, [contactAPIUrl]);

    
    const contactMatchbyCIDN = useCallback(async (cidn: Number| null ) => {
        try {
        
        const requestData = {
            cidn: Number(cidn)
        };
       
        const response = await axios.post(`${contactAPIUrl}/contact/ContactMatchByCIDN`, requestData);
        return response.data;

        } catch (error) {
        console.error("Error finding contact match:", error);
        throw error;
        }
    }, [contactAPIUrl]);

const AddManualContactMatch = useCallback(async (cidn: Number, cidn_match: number, addedby: string) => {
        try {
        const token = CookieService.getAuthToken();

        const requestData = {
            cidn: Number(cidn),
            cidn_match: Number(cidn_match),
            addedby: addedby
        };

        const response = await axios.post(`${contactAPIUrl}/contact/AddManualContactMatch`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;


        } catch (error) {
        console.error("Error adding Manual Contact Match:", error);
        throw error;
        }
    }, [contactAPIUrl]);

const getEngines = useCallback(async (engineid: number, engineFilters: string) => {
        try {
           const token = CookieService.getAuthToken();
             const response = await axios.get(`${apiUrl}/espec/GetEngines`, {
                params: {
                    engineid,
                    engineFilters
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
                
            

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get engines');
            }
            throw error;
        }
    }, [apiUrl]);


    const getEngineFilters = useCallback(async () => {
        try {
           const token = CookieService.getAuthToken();
             const response = await axios.get(`${apiUrl}/espec/GetEngineFilters`, {
                
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
                
            

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get engine Filters');
            }
            throw error;
        }
    }, [apiUrl]);


    const getEngineComp = useCallback(async (engineid: number) => {
        try {
           const token = CookieService.getAuthToken();
             const response = await axios.get(`${apiUrl}/espec/GetEnginecomp`, {
                params: {
                    engineid
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
                
            

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get engine comparisons');
            }
            throw error;
        }
    }, [apiUrl]);

   const getEngineImages = useCallback(async (engineid: number) => {
        try {
           const token = CookieService.getAuthToken();
             const response = await axios.get(`${apiUrl}/espec/GetEngineImages`, {
                params: {
                    engineid
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
                
            

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get engine images');
            }
            throw error;
        }
    }, [apiUrl]);

const getUserMessages = useCallback(async (userid: string) => {
        try {
           const token = CookieService.getAuthToken();
             const response = await axios.get(`${apiUrl}/users/UserMessages`, {
                params: {
                    userid
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
                
            

            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to get user messages');
            }
            throw error;
        }
    }, [apiUrl]);


    const UpdateUserMessage = useCallback(async (messageid: Number, userid: string) => {
        try {
        const token = CookieService.getAuthToken();
        const requestData = {
            MessageID: Number(messageid),
            userID: userid            
        };

        
        const response = await axios.post(`${apiUrl}/users/UpdateUserMessage`, requestData,{
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },

            });
            return response.data;


        } catch (error) {
        console.error("Error updating message:", error);
        throw error;
        }
    }, [apiUrl]);





    return {
        verifyToken,
        getUsers,
        getUserById,
        getUserRoles,
        assignRoleToUser,
        removeRoleFromUser,
        createUser,
        updateUser,
        login,
        register,
        forgotPassword,
        resetPassword,
        updateUserPassword,
        getRoles,
        createRole,
        updateRole,
        getPermissions,
        createPermission,
        updatePermission,
        deleteRole,
        getRolePermissions,
        assignPermissionToRole,
        removePermissionFromRole,
        getUserPermissions,
        assignPermissionToUser,
        removePermissionFromUser,
        getMyRoles,
        getMyPermissions,
        getUserRegisteredRoleId,
        deleteUser,
        getBuisnessFile,
        addUserTracking,
        getContractStatuses,
        getContractTypes,
        getUserContractStatus,
        getActivityTypes,
        getUserActivity,
        getUserTrackingPages,
        getNewsOtp,
        getOpt,
        postSendReqInfoEmail,
        getUserInfoReportsList,
        getUserInfoReport,
        postUserData,
        getStateProvince,
        getUsersDropdwn,
        confirmEmail,
        getUserAgreements,
        saveContact,
        getContacts,
        deleteContact,
        deleteContactPhone,
        getContactByCIDN,
        getUsersByContactCIDN,
        getContactAddressByCIDN,
        getContactEmailByCIDN,
        getContactPhoneByCIDN,
        getContactCompanyByCIDN,
        getContactCRMByCIDN,
        updateContactPhone,
        addContactPhone,
        updateContactCompany,
        addContactCompany,
        deleteContactCompany,
        updateContactEmail,
        addContactEmail,
        deleteContactEmail,
        updateContactAddress,
        addContactAddress,
        deleteContactAddress,
        addUserToContact,
        checkEmailExists,
        getContactByEmail,
        updateContact,
        updateUserPrimaryInd,
        copyContactData, 
        checkContactForMatch,
        removeUserFromContact,
        UpdateAgreementSetInactive,
        getContactsToMatch  ,
        UpdateContactMatchStatus ,
        ContactMatch,
        contactMatchbyCIDN,
        AddManualContactMatch,
        getEngines,
        getEngineFilters,
        getEngineComp,
        getEngineImages,
        getUserMessages,
        UpdateUserMessage
    };
};

export default useAPI;
